import React__default from 'react';
import { FieldValid } from '../Field/Validation/types.js';
import debounce from '../../Utils/debouncer.js';
import { isFormValid, isValidationValid } from './utils.js';
import { cloneDeep, setDeepProp, getDeepProp } from '../../../utils/js.js';
import validateFields from './multiFieldValidation.js';

/* eslint-disable max-classes-per-file */
const FormChangedDelay = 100;
function getStateFieldValue(name, state) {
    return typeof name === 'string' ? getDeepProp(name, state) : state[name];
}
class MultiFieldValidator {
    constructor() {
        this.suspended = false;
        this.validationActions = [];
    }
    execute(action) {
        if (this.suspended) {
            this.validationActions.push(action);
        }
        else {
            action();
        }
    }
    suspendWhileExecuting(action) {
        try {
            this.suspended = true;
            action();
        }
        finally {
            this.reactivate();
        }
    }
    reactivate() {
        this.suspended = false;
        const actions = [...this.validationActions];
        this.validationActions = [];
        setTimeout(() => {
            actions.forEach((action) => action());
        }, 100); // aspetto che react cambi lo stato dei componenti
    }
}
class FormContextValue {
    constructor(initValues) {
        /**
         * Specifica se la form è stata costruita
         */
        this.isMounted = false;
        /*
          Validazioni multiple.
        */
        this.multiFieldValidations = [];
        this.multiFieldValidator = new MultiFieldValidator();
        this.formValueChanged = debounce((isOnInitialization) => {
            if (!this.onFormChange)
                return;
            const values = this.getValues();
            const validations = this.getValidations();
            this.onFormChange(isFormValid(validations), values, validations, isOnInitialization);
        }, FormChangedDelay);
        this.setValues = (values) => {
            this.multiFieldValidator.suspendWhileExecuting(() => {
                this.getComponentFieldNames().forEach((name) => {
                    this.setFieldValue(name, values ? getStateFieldValue(name, values) : undefined);
                });
            });
            if (values) {
                this.initialValues = cloneDeep(values);
            }
        };
        this.initializeValues = (values) => {
            this.multiFieldValidator.suspendWhileExecuting(() => {
                this.getComponentFieldNames().forEach((name) => {
                    this.initializeFieldValue(name, values ? getStateFieldValue(name, values) : undefined);
                });
            });
            if (values) {
                const newValues = cloneDeep(values);
                this.initialValues = newValues;
            }
        };
        this.changeValues = (values) => {
            this.multiFieldValidator.suspendWhileExecuting(() => {
                this.getComponentFieldNames().forEach((name) => {
                    this.changeFieldValue(name, values ? getStateFieldValue(name, values) : undefined);
                });
            });
            if (values) {
                const newValues = cloneDeep(values);
                this.initialValues = newValues;
            }
        };
        this.getValues = () => {
            const values = this.initialValues ? cloneDeep(this.initialValues) : {};
            if (this.isMounted) {
                Object
                    .entries(this.fieldComponentRefs)
                    .forEach(([fieldName, componentRef]) => {
                    setDeepProp(fieldName, componentRef.current.getValue(), values, values);
                });
            }
            return values;
        };
        this.isValid = () => !Object
            .values(this.fieldComponentRefs)
            .some((componentRef) => !isValidationValid(componentRef.current.getValidation() || FieldValid));
        this.getValidations = () => {
            const validations = {};
            Object
                .entries(this.fieldComponentRefs)
                .forEach(([fieldName, componentRef]) => {
                validations[fieldName] = componentRef.current.getValidation() || FieldValid;
            });
            return validations;
        };
        this.validate = () => {
            Object
                .values(this.fieldComponentRefs)
                .forEach((componentRef) => {
                componentRef.current.validate();
            });
        };
        this.isAllFieldRegistered = (fieldNames) => !fieldNames.some((fieldName) => this.fieldComponentRefs[fieldName] === undefined);
        this.fieldComponentRefs = {};
        this.initialValues = initValues ? cloneDeep(initValues) : {};
    }
    getComponentFieldNames() {
        return Object.keys(this.fieldComponentRefs);
    }
    setFieldValue(name, value) {
        const component = this.fieldComponentRefs[name];
        if (component) {
            component.current.setValue(value);
        }
    }
    initializeFieldValue(name, value) {
        const component = this.fieldComponentRefs[name];
        if (component) {
            component.current.initializeValue(value);
        }
    }
    changeFieldValue(name, value) {
        const component = this.fieldComponentRefs[name];
        if (component) {
            component.current.changeValue(value);
        }
    }
    /**
       * funzione di regitrazione del componente.
       * In componente deve avere initializeValue e validate.
       */
    registerComponent(name, componentRef) {
        this.unregisterComponent(name);
        this.fieldComponentRefs[name] = componentRef;
        const initValue = getStateFieldValue(name, this.initialValues);
        componentRef.current.initializeValue(initValue);
    }
    /**
       * funzione di deregitrazione del componente.
       */
    unregisterComponent(name) {
        const component = this.fieldComponentRefs[name];
        if (component) {
            const value = component.current.getValue();
            setDeepProp(name, value, this.initialValues, this.initialValues);
            delete this.fieldComponentRefs[name];
            this.formValueChanged(false);
        }
    }
    valueChanged(name, value, validation, isOnInitialization) {
        var _a;
        this.formValueChanged(isOnInitialization);
        (_a = this.onValueChange) === null || _a === void 0 ? void 0 : _a.call(this, name, value, validation, isOnInitialization);
    }
    validateByMultiField(fieldName, value, isOnInitialization) {
        const multiFieldValsRegisteredComps = this.multiFieldValidations.filter((mfv) => this.isAllFieldRegistered(mfv.fieldNames));
        if (multiFieldValsRegisteredComps.length === 0)
            return;
        this.multiFieldValidator.execute(() => {
            const values = this.getValues();
            setDeepProp(fieldName, value, values, values);
            validateFields(fieldName, values, this.multiFieldValidations, isOnInitialization, (name) => { var _a; return (_a = this.fieldComponentRefs[name]) === null || _a === void 0 ? void 0 : _a.current; });
        });
    }
    mounted() {
        this.isMounted = true;
    }
    unmounted() {
        this.isMounted = false;
        this.onFormChange = undefined;
        this.onValueChange = undefined;
    }
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FormContext = React__default.createContext(new FormContextValue());

export { FormChangedDelay, FormContextValue, FormContext as default };
